import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleTwoTone,
  DeleteOutlined,
  PlusOutlined,
  StarTwoTone,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { api } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import {
  adminPageWrap,
  adminAlertStyle,
  adminFilterCardStyle,
  adminContentCardStyle,
} from '../styles/adminPageShell';

const { Text, Paragraph } = Typography;

const ANCHOR_SCORES = ['1', '2', '3', '4', '5'] as const;
const OTHER_CODE = 'OTHER';

type HpDim = {
  code: string;
  name: string;
  desc: string;
  anchors: Record<string, string>;
};
type HpPath = { code: string; name: string };

type HpConfigApi = {
  batchPhase: string | null;
  config:
    | (Record<string, unknown> & {
        dimensions?: HpDim[];
        developPaths?: HpPath[];
        thresholds?: Record<string, number>;
        starRequired?: number;
        starWindowStart?: string;
        starWindowEnd?: string;
      })
    | null;
  coverage: {
    expectedEvaluatees: number;
    leaderCount: number;
    submitted: number;
    nominatedTrue: number;
    locked: number;
    withRecord: number;
    notStarted: number;
  } | null;
};

/** 批次阶段英文 enum 转业务用户能看懂的中文。 */
const BATCH_PHASE_CN: Record<string, string> = {
  DRAFT: '草稿',
  ONGOING: '进行中',
  CALCULATING: '核算中',
  COMPLETED: '已完成',
  LOCKED: '已锁定',
};
function batchPhaseCn(phase?: string | null): string {
  if (!phase) return '';
  return BATCH_PHASE_CN[phase] || phase;
}

function emptyAnchors(): Record<string, string> {
  return { '1': '', '2': '', '3': '', '4': '', '5': '' };
}

export function HpAdminPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { batches, batchId, setBatchId, isLoading: batchesLoading } = useBatches();

  const [dimensions, setDimensions] = useState<HpDim[]>([]);
  const [developPaths, setDevelopPaths] = useState<HpPath[]>([]);
  const [avgMin, setAvgMin] = useState<number | null>(4.0);
  const [perDimMin, setPerDimMin] = useState<number | null>(3);
  const [starMin, setStarMin] = useState<number | null>(2);
  const [starRequired, setStarRequired] = useState<number | null>(2);
  const [starRange, setStarRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  useEffect(() => {
    const q = searchParams.get('batchId');
    if (q && Number.isFinite(Number(q))) setBatchId(Number(q));
  }, [searchParams, setBatchId]);

  const syncUrl = (bid?: number) => {
    const next = new URLSearchParams(searchParams);
    if (bid != null) next.set('batchId', String(bid));
    setSearchParams(next, { replace: true });
  };

  const { data: hpMeta, isLoading: configLoading } = useQuery({
    queryKey: ['hp-config', batchId],
    queryFn: async () =>
      api.get('/admin/high-potential/config', { params: { batchId } }) as Promise<HpConfigApi>,
    enabled: !!batchId,
  });

  const cfg = hpMeta?.config ?? null;
  const coverage = hpMeta?.coverage ?? undefined;
  const batchPhase = hpMeta?.batchPhase ?? undefined;

  useEffect(() => {
    if (!cfg || batchId == null) return;
    const dims = Array.isArray(cfg.dimensions) ? cfg.dimensions : [];
    setDimensions(
      dims.map((d) => ({
        code: d.code,
        name: d.name ?? '',
        desc: d.desc ?? '',
        anchors: {
          '1': d.anchors?.['1'] ?? '',
          '2': d.anchors?.['2'] ?? '',
          '3': d.anchors?.['3'] ?? '',
          '4': d.anchors?.['4'] ?? '',
          '5': d.anchors?.['5'] ?? '',
        },
      })),
    );
    setDevelopPaths(
      Array.isArray(cfg.developPaths)
        ? cfg.developPaths.map((p) => ({ code: p.code, name: p.name ?? '' }))
        : [],
    );
    const th = (cfg.thresholds ?? {}) as Record<string, number>;
    setAvgMin(th.avg_min ?? 4.0);
    setPerDimMin(th.per_dim_min ?? 3);
    setStarMin(th.star_min ?? 2);
    setStarRequired((cfg.starRequired as number) ?? 2);
    const s = cfg.starWindowStart ? dayjs(String(cfg.starWindowStart)) : null;
    const e = cfg.starWindowEnd ? dayjs(String(cfg.starWindowEnd)) : null;
    if (s?.isValid() && e?.isValid()) setStarRange([s, e]);
  }, [cfg, batchId]);

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.put('/admin/high-potential/config', payload),
    onSuccess: () => {
      message.success('高潜问卷已保存，领导端立即生效');
      qc.invalidateQueries({ queryKey: ['hp-config', batchId] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '保存失败');
    },
  });

  const updateDim = (i: number, patch: Partial<HpDim>) =>
    setDimensions((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const updateAnchor = (i: number, n: string, val: string) =>
    setDimensions((prev) =>
      prev.map((d, idx) => (idx === i ? { ...d, anchors: { ...d.anchors, [n]: val } } : d)),
    );
  const updatePath = (i: number, name: string) =>
    setDevelopPaths((prev) => prev.map((p, idx) => (idx === i ? { ...p, name } : p)));
  const removePath = (i: number) =>
    setDevelopPaths((prev) => prev.filter((_, idx) => idx !== i));
  const addPath = () =>
    setDevelopPaths((prev) => [
      ...prev,
      { code: `OPT_${Date.now().toString(36)}`, name: '' },
    ]);
  const addOther = () =>
    setDevelopPaths((prev) => [...prev, { code: OTHER_CODE, name: '其他（写清建议方向）' }]);
  const hasOther = developPaths.some((p) => p.code === OTHER_CODE);

  const onSaveConfig = () => {
    if (!batchId) return;
    if (dimensions.length === 0) {
      message.error('请先「一键初始化」生成默认四维，再编辑保存');
      return;
    }
    if (dimensions.some((d) => !d.name.trim())) {
      message.error('每个维度都要填名称');
      return;
    }
    const paths = developPaths.filter((p) => p.name.trim());
    if (paths.length === 0) {
      message.error('至少配置一个培养方向');
      return;
    }
    if (!starRange?.[0]?.isValid() || !starRange?.[1]?.isValid()) {
      message.error('请选择 STAR 证据时间窗口');
      return;
    }
    saveMutation.mutate({
      batchId,
      dimensions,
      developPaths: paths,
      thresholds: {
        avg_min: avgMin ?? 4.0,
        per_dim_min: perDimMin ?? 3,
        star_min: starMin ?? 2,
      },
      starRequired: starRequired ?? 2,
      starWindowStart: starRange[0].startOf('day').toISOString(),
      starWindowEnd: starRange[1].endOf('day').toISOString(),
    });
  };

  const initDefaults = () => {
    if (!batchId) return;
    saveMutation.mutate({ batchId });
  };

  const batchSelect = (
    <Select
      placeholder="批次"
      style={{ width: 220 }}
      loading={batchesLoading}
      value={batchId}
      options={batches.map((b) => ({ label: b.name, value: b.id }))}
      onChange={(v) => {
        setBatchId(v);
        syncUrl(v);
      }}
    />
  );

  const coverageCard =
    coverage != null ? (
      <Card style={{ ...adminFilterCardStyle, marginBottom: 12 }} styles={{ body: { paddingBottom: 12 } }}>
        <Space wrap size={12} style={{ marginBottom: 4 }}>
          {batchSelect}
          <Tag color="purple">V2 高潜四维 + STAR + 培养方向（后台可改）</Tag>
          {batchPhase ? <Tag>批次状态：{batchPhaseCn(batchPhase)}</Tag> : null}
        </Space>
        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={5}>
            <Statistic
              title="应评下属"
              value={coverage.expectedEvaluatees}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              suffix="人"
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="直属领导"
              value={coverage.leaderCount}
              prefix={<UserOutlined style={{ color: '#1677ff' }} />}
              suffix="人"
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="已提交"
              value={coverage.submitted}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              suffix={coverage.expectedEvaluatees ? `/ ${coverage.expectedEvaluatees}` : ''}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="识别为高潜"
              value={coverage.nominatedTrue}
              valueStyle={{ color: '#eb2f96' }}
              prefix={<StarTwoTone twoToneColor="#eb2f96" />}
              suffix="人"
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="完成率"
              value={
                coverage.expectedEvaluatees
                  ? Math.round((coverage.submitted * 100) / coverage.expectedEvaluatees)
                  : 0
              }
              suffix="%"
              valueStyle={{
                color:
                  coverage.expectedEvaluatees && coverage.submitted >= coverage.expectedEvaluatees
                    ? '#52c41a'
                    : '#1677ff',
              }}
            />
          </Col>
        </Row>
        <Progress
          percent={
            coverage.expectedEvaluatees
              ? Math.round((coverage.submitted * 100) / coverage.expectedEvaluatees)
              : 0
          }
          size="small"
          showInfo={false}
          strokeColor={{ '0%': '#722ed1', '100%': '#52c41a' }}
          style={{ marginTop: 8 }}
        />
        <Row gutter={16} style={{ marginTop: 6 }}>
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              尚无任何提交记录：<b>{coverage.notStarted}</b> 人
              {coverage.locked > 0 ? <> · 冻结锁定：<b>{coverage.locked}</b> 条</> : null}
              {coverage.withRecord != null ? <> · 已建记录：<b>{coverage.withRecord}</b></> : null}
            </Text>
          </Col>
        </Row>
      </Card>
    ) : (
      <Card style={{ ...adminFilterCardStyle, marginBottom: 12 }}>
        <Space wrap>
          {batchSelect}
          <Tag color="purple">V2 高潜四维 + STAR + 培养方向（后台可改）</Tag>
          {batchPhase ? <Tag>批次状态：{batchPhaseCn(batchPhase)}</Tag> : null}
        </Space>
      </Card>
    );

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">高潜识别 · 题库与阈值</div>

      <Alert
        style={adminAlertStyle}
        type="info"
        showIcon
        message="高潜的题目、锚定描述与培养方向都在这里维护，保存后领导端（电脑 / 手机）立即同步"
        description={
          <Paragraph style={{ marginBottom: 0 }} type="secondary">
            高潜识别已合并到「岗位胜任力评价」表单尾部，由部门负责人在评胜任力时直接判定。
            本页配置四个维度的名称 / 说明 / 每档 1-5 分锚定描述、培养方向选项、STAR 时间窗与阈值，
            全部改成可视化录入（不再需要写 JSON），保存后即用于领导端表单与核算定级。
          </Paragraph>
        }
      />

      {coverageCard}

      <Card style={adminContentCardStyle} loading={configLoading}>
        {!cfg ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              这个批次还没配高潜问卷。点下面按钮，系统会先帮你填好一套默认题目（四个评分维度、每档怎么打分、培养方向选项），你再改改、保存就行。
            </Text>
            <Button type="primary" onClick={initDefaults} loading={saveMutation.isPending}>
              启用默认模板
            </Button>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Text type="secondary">
              修改后点击底部「保存问卷与阈值」；领导端提交与后端校验将立即使用新题目、新阈值与时间窗。
            </Text>

            <Divider orientation="left" style={{ margin: '4px 0' }}>
              一、四维题库（名称 / 说明 / 每档锚定）
            </Divider>
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              四个维度（A/B/C/D）为固定框架，可改名称、说明与每档 1-5 分的锚定描述；锚定会原样展示给评价领导，帮助逐档对照打分。
            </Paragraph>
            {dimensions.map((dim, i) => (
              <Card
                key={dim.code}
                size="small"
                title={
                  <Space>
                    <Tag color="gold">维度 {dim.code}</Tag>
                    <span style={{ fontWeight: 600 }}>{dim.name || '（未命名）'}</span>
                  </Space>
                }
              >
                <Row gutter={12}>
                  <Col span={24} md={8}>
                    <Text strong>维度名称</Text>
                    <Input
                      value={dim.name}
                      onChange={(e) => updateDim(i, { name: e.target.value })}
                      placeholder="如：成长性"
                      style={{ marginTop: 6 }}
                    />
                  </Col>
                  <Col span={24} md={16}>
                    <Text strong>一句话说明</Text>
                    <Input
                      value={dim.desc}
                      onChange={(e) => updateDim(i, { desc: e.target.value })}
                      placeholder="如：过去 12 月的能力跃迁速度"
                      style={{ marginTop: 6 }}
                    />
                  </Col>
                </Row>
                <div style={{ marginTop: 12 }}>
                  <Text strong>每档锚定描述</Text>
                  {ANCHOR_SCORES.map((n) => (
                    <div
                      key={n}
                      style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8 }}
                    >
                      <Tag color="blue" style={{ minWidth: 44, textAlign: 'center', marginTop: 4 }}>
                        {n} 分
                      </Tag>
                      <Input.TextArea
                        value={dim.anchors[n]}
                        onChange={(e) => updateAnchor(i, n, e.target.value)}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        placeholder={`第 ${n} 档行为锚定描述`}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            ))}

            <Divider orientation="left" style={{ margin: '4px 0' }}>
              二、培养方向（可增删改）
            </Divider>
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              领导识别某人为高潜后，从这里勾选「下一阶段培养重点」。带「自由填写」标记的那一项，领导勾选后可手填补充说明。
            </Paragraph>
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              {developPaths.map((p, i) => (
                <Row key={p.code} gutter={8} align="middle" wrap={false}>
                  <Col flex="auto">
                    <Input
                      value={p.name}
                      onChange={(e) => updatePath(i, e.target.value)}
                      placeholder="培养方向描述"
                      addonBefore={p.code === OTHER_CODE ? '自由填写' : `${i + 1}`}
                    />
                  </Col>
                  <Col flex="none">
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => removePath(i)}
                    />
                  </Col>
                </Row>
              ))}
              <Space wrap>
                <Button icon={<PlusOutlined />} onClick={addPath}>
                  添加培养方向
                </Button>
                {!hasOther ? (
                  <Button icon={<PlusOutlined />} onClick={addOther}>
                    添加「其他（自由填写）」项
                  </Button>
                ) : null}
              </Space>
            </Space>

            <Divider orientation="left" style={{ margin: '4px 0' }}>
              三、阈值与 STAR 时间窗
            </Divider>
            <Row gutter={16}>
              <Col span={24} md={8}>
                <Text strong>核算/候选：四维均值 ≥</Text>
                <InputNumber
                  min={1}
                  max={5}
                  step={0.1}
                  value={avgMin}
                  onChange={setAvgMin}
                  style={{ marginTop: 8, width: '100%' }}
                />
              </Col>
              <Col span={24} md={8}>
                <Text strong>提交校验：单维最低分 ≥</Text>
                <InputNumber
                  min={1}
                  max={5}
                  value={perDimMin}
                  onChange={setPerDimMin}
                  style={{ marginTop: 8, width: '100%' }}
                />
              </Col>
              <Col span={24} md={8}>
                <Text strong>提交校验：STAR 最少条数</Text>
                <InputNumber
                  min={1}
                  max={20}
                  value={starMin}
                  onChange={setStarMin}
                  style={{ marginTop: 8, width: '100%' }}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24} md={8}>
                <Text strong>题目要求 STAR 条数（与上项通常一致）</Text>
                <InputNumber
                  min={1}
                  max={20}
                  value={starRequired}
                  onChange={setStarRequired}
                  style={{ marginTop: 8, width: '100%' }}
                />
              </Col>
              <Col span={24} md={16}>
                <Text strong>STAR 证据必须有一条落在此时间窗内</Text>
                <div style={{ marginTop: 8 }}>
                  <DatePicker.RangePicker
                    style={{ width: '100%' }}
                    value={starRange}
                    onChange={(v) => setStarRange(v as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                  />
                </div>
              </Col>
            </Row>

            <Button type="primary" onClick={onSaveConfig} loading={saveMutation.isPending}>
              保存问卷与阈值
            </Button>
          </Space>
        )}
      </Card>

      <Paragraph style={{ marginTop: 16 }} type="secondary">
        批次流程与推送见{' '}
        <Link to={batchId ? `/batches/${batchId}` : '/batches'}>盘点批次详情</Link>
        （开启批次后通知评价人）。
      </Paragraph>
    </div>
  );
}
